import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PlayCircle, X, User, Mail, Phone, Calendar, FileText, Server, Gift } from 'lucide-react';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  Field,
  Input,
  Spinner,
} from '@pnl/ui';
import type { PendingActivation } from '@pnl/sdk';
import { PnlApiError } from '@pnl/sdk';
import { api } from '../lib/api';

function fmtBrl(cents: number): string {
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function ActivationsPage() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<PendingActivation | null>(null);
  const [showManual, setShowManual] = useState(false);

  // A fila mostra por padrão só quem SOLICITOU a ativação no app. Quem apenas
  // comprou fica na tela de Contratos até assinar e pedir a ativação — que pode
  // levar dias (pedido do cliente, jul/2026).
  const [showAll, setShowAll] = useState(false);

  const query = useQuery({
    queryKey: ['admin', 'activations', { all: showAll }],
    queryFn: () => api.listPendingActivations(showAll ? { all: true } : undefined),
  });

  const activate = useMutation({
    mutationFn: ({
      subscriptionId,
      subAccountNelogicaId,
      notes,
    }: {
      subscriptionId: string;
      subAccountNelogicaId: string;
      notes?: string;
    }) =>
      api.activateSubscription(subscriptionId, {
        subAccountNelogicaId,
        ...(notes ? { notes } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'activations'] });
      setSelected(null);
    },
  });

  const items = query.data?.items ?? [];
  const awaitingRequest = query.data?.awaitingRequest ?? 0;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-2xs text-brand-500 font-semibold uppercase tracking-widest">
            Operação
          </p>
          <h1 className="font-display text-ink-900 mt-1 text-3xl font-semibold tracking-tight">
            Ativações pendentes
          </h1>
          <p className="text-ink-500 mt-1 text-sm">
            Traders que <strong>solicitaram a ativação</strong> no app e aguardam criação manual da
            conta na Profit/BlackArrow.
          </p>
        </div>
        <Button leadingIcon={<Gift size={15} />} onClick={() => setShowManual(true)}>
          Ativar manualmente
        </Button>
      </div>

      {/* Quem comprou mas ainda não pediu ativação não some: fica contabilizado
          aqui, com atalho pra tela onde ele realmente vive (Contratos). */}
      {awaitingRequest > 0 && !showAll && (
        <Alert tone="info" title={`${awaitingRequest} compra(s) ainda sem pedido de ativação`}>
          Já pagaram, mas ainda não clicaram em “Ativar plano” no app — normalmente porque o
          contrato ainda não foi assinado. Acompanhe em{' '}
          <Link to="/contracts" className="text-brand-600 font-semibold underline">
            Contratos
          </Link>
          , ou{' '}
          <button
            type="button"
            onClick={() => setShowAll(true)}
            className="text-brand-600 font-semibold underline"
          >
            inclua na fila
          </button>
          .
        </Alert>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <CardTitle>
              Fila ({items.length}
              {query.isLoading ? '…' : ''})
            </CardTitle>
            {showAll && (
              <button
                type="button"
                onClick={() => setShowAll(false)}
                className="text-ink-500 hover:text-ink-800 text-xs font-semibold underline"
              >
                Mostrar só as solicitadas
              </button>
            )}
          </div>
        </CardHeader>
        <CardBody>
          {query.isLoading ? (
            <div className="flex justify-center py-10">
              <Spinner />
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={<PlayCircle size={28} />}
              title="Nenhuma ativação pendente"
              description="O trader aparece aqui quando clica em “Ativar plano” no app, depois de assinar o contrato."
            />
          ) : (
            <div className="divide-ink-100 divide-y">
              {items.map((item) => (
                <button
                  type="button"
                  key={item.subscriptionId}
                  onClick={() => setSelected(item)}
                  className="hover:bg-ink-50/60 flex w-full items-center justify-between gap-4 py-4 text-left transition-colors"
                >
                  <div className="flex-1">
                    <p className="text-ink-900 text-sm font-semibold">
                      {item.fullName}{' '}
                      <span className="text-ink-500 font-normal">· {item.userEmail}</span>
                    </p>
                    <p className="text-ink-500 mt-0.5 text-xs">
                      {item.planCode} ({item.marketCode}) · pago em{' '}
                      {item.paidAt ? new Date(item.paidAt).toLocaleString('pt-BR') : '—'}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1.5">
                    {item.activationRequestedAt && (
                      <Badge tone="brand" dot>
                        Solicitado pelo trader
                      </Badge>
                    )}
                    <Badge tone={item.contractStatus === 'SIGNED' ? 'success' : 'warning'}>
                      Contrato {item.contractStatus ?? 'sem registro'}
                    </Badge>
                  </div>
                </button>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      <Alert tone="info" title="Como funciona">
        Trader paga (webhook Stripe/Mercado Pago) → Autentique gera o contrato → trader assina
        (webhook) → <strong>trader clica “Ativar plano” no app</strong> e só então entra nesta fila.
        Antes disso ele fica em{' '}
        <Link to="/contracts" className="text-brand-600 font-semibold underline">
          Contratos
        </Link>
        , onde dá pra acompanhar a assinatura e reenviar o documento. Aqui o admin cria a conta com
        o ID da Nelogica/BlackArrow.
      </Alert>

      {selected && (
        <ActivationDrawer
          item={selected}
          submitting={activate.isPending}
          error={activate.error}
          onClose={() => {
            activate.reset();
            setSelected(null);
          }}
          onSubmit={(subAccountId, notes) =>
            activate.mutate({
              subscriptionId: selected.subscriptionId,
              subAccountNelogicaId: subAccountId,
              ...(notes ? { notes } : {}),
            })
          }
        />
      )}

      {showManual && (
        <ManualActivationDrawer
          onClose={() => setShowManual(false)}
          onDone={() => {
            void qc.invalidateQueries({ queryKey: ['admin', 'activations'] });
            void qc.invalidateQueries({ queryKey: ['admin', 'accounts'] });
            setShowManual(false);
          }}
        />
      )}
    </div>
  );
}

/**
 * Ativação manual (cortesia / sem compra): busca o trader por email, escolhe o
 * plano e o ID da subconta. Cria assinatura ACTIVE manual + provisiona a conta.
 */
function ManualActivationDrawer({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [q, setQ] = useState('');
  const [userId, setUserId] = useState<string | null>(null);
  const [userLabel, setUserLabel] = useState('');
  const [planId, setPlanId] = useState('');
  const [subAccountId, setSubAccountId] = useState('');
  const [notes, setNotes] = useState('');

  const usersQuery = useQuery({
    queryKey: ['admin', 'users', 'search', q],
    queryFn: () => api.listUsers({ q, page: 1, pageSize: 8 }),
    enabled: q.trim().length >= 2 && !userId,
  });
  const plansQuery = useQuery({
    queryKey: ['admin', 'plans'],
    queryFn: () => api.listAdminPlans(),
  });

  const manual = useMutation({
    mutationFn: () =>
      api.manualActivate({
        userId: userId!,
        planId,
        subAccountNelogicaId: subAccountId.trim(),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      }),
    onSuccess: onDone,
  });

  const errorMessage =
    manual.error instanceof PnlApiError
      ? manual.error.api.message
      : manual.error
        ? 'Falha ao ativar manualmente.'
        : null;

  const canSubmit = !!userId && !!planId && !!subAccountId.trim() && !manual.isPending;

  return (
    <div
      className="bg-ink-900/40 fixed inset-0 z-40 flex justify-end"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="border-ink-200 shadow-lift w-full max-w-md overflow-y-auto border-l bg-white p-6"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Ativação manual"
      >
        <div className="flex items-start justify-between">
          <div>
            <p className="text-2xs text-brand-500 font-semibold uppercase tracking-widest">
              Cortesia / sem compra
            </p>
            <h2 className="text-ink-900 mt-1 text-xl font-semibold">Ativar manualmente</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-ink-500 hover:bg-ink-100 rounded p-1.5 transition-colors"
            aria-label="Fechar"
          >
            <X size={18} />
          </button>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) manual.mutate();
          }}
          className="mt-6 space-y-4"
        >
          <Field label="Trader (busca por nome ou email)" required>
            {userId ? (
              <div className="border-ink-200 flex items-center justify-between rounded-lg border px-3 py-2">
                <span className="text-ink-900 text-sm font-medium">{userLabel}</span>
                <button
                  type="button"
                  className="text-brand-600 text-xs font-semibold"
                  onClick={() => {
                    setUserId(null);
                    setUserLabel('');
                  }}
                >
                  trocar
                </button>
              </div>
            ) : (
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Digite ao menos 2 caracteres…"
                autoFocus
              />
            )}
          </Field>

          {!userId && q.trim().length >= 2 && (
            <div className="border-ink-100 max-h-44 overflow-y-auto rounded-lg border">
              {usersQuery.isLoading ? (
                <div className="flex justify-center py-4">
                  <Spinner size={16} />
                </div>
              ) : (usersQuery.data?.items.length ?? 0) === 0 ? (
                <p className="text-ink-500 px-3 py-3 text-xs">Nenhum usuário encontrado.</p>
              ) : (
                usersQuery.data!.items.map((u) => (
                  <button
                    type="button"
                    key={u.id}
                    onClick={() => {
                      setUserId(u.id);
                      setUserLabel(`${u.fullName} · ${u.email}`);
                    }}
                    className="hover:bg-ink-50 block w-full px-3 py-2 text-left text-sm"
                  >
                    <span className="text-ink-900 font-medium">{u.fullName}</span>{' '}
                    <span className="text-ink-500">· {u.email}</span>
                  </button>
                ))
              )}
            </div>
          )}

          <Field label="Plano" required>
            <select
              value={planId}
              onChange={(e) => setPlanId(e.target.value)}
              required
              className="border-ink-200 focus:border-brand-500 focus:ring-brand-500/30 w-full rounded-lg border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2"
            >
              <option value="">Selecione…</option>
              {(plansQuery.data?.items ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.marketCode}) · {fmtBrl(p.capitalInitialCents)}
                </option>
              ))}
            </select>
          </Field>

          <Field
            label="ID da subconta Nelogica/BlackArrow"
            required
            hint="ID interno da conta criada pelo time de operações na plataforma"
          >
            <Input
              value={subAccountId}
              onChange={(e) => setSubAccountId(e.target.value)}
              placeholder="ex: 12345678"
              required
            />
          </Field>

          <Field label="Notas internas (opcional)">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="border-ink-200 focus:border-brand-500 focus:ring-brand-500/30 w-full rounded-lg border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2"
              placeholder="Ex: cortesia parceiro, reposição de conta…"
            />
          </Field>

          {errorMessage && (
            <Alert tone="danger" title="Não foi possível ativar">
              {errorMessage}
            </Alert>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {manual.isPending ? 'Ativando…' : 'Criar conta'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface DrawerProps {
  item: PendingActivation;
  submitting: boolean;
  error: Error | null;
  onClose: () => void;
  onSubmit: (subAccountNelogicaId: string, notes?: string) => void;
}

function ActivationDrawer({ item, submitting, error, onClose, onSubmit }: DrawerProps) {
  const [subAccountId, setSubAccountId] = useState('');
  const [notes, setNotes] = useState('');

  const errorMessage =
    error instanceof PnlApiError
      ? error.api.message
      : error
        ? 'Falha ao ativar a assinatura.'
        : null;

  return (
    <div
      className="bg-ink-900/40 fixed inset-0 z-40 flex justify-end"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="border-ink-200 shadow-lift w-full max-w-md overflow-y-auto border-l bg-white p-6"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Ativar assinatura"
      >
        <div className="flex items-start justify-between">
          <div>
            <p className="text-2xs text-brand-500 font-semibold uppercase tracking-widest">
              Ativar assinatura
            </p>
            <h2 className="text-ink-900 mt-1 text-xl font-semibold">{item.fullName}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-ink-500 hover:bg-ink-100 rounded p-1.5 transition-colors"
            aria-label="Fechar"
          >
            <X size={18} />
          </button>
        </div>

        <dl className="border-ink-200 mt-5 space-y-2 rounded-lg border p-4 text-sm">
          <Row icon={<User size={14} />} label="Nome" value={item.fullName} />
          <Row icon={<Mail size={14} />} label="Email" value={item.userEmail} />
          <Row icon={<FileText size={14} />} label="Documento" value={item.documentNumber ?? '—'} />
          <Row icon={<Phone size={14} />} label="Telefone" value={item.phone ?? '—'} />
          <Row icon={<Calendar size={14} />} label="Nascimento" value={item.birthDate ?? '—'} />
          <Row
            icon={<Server size={14} />}
            label="Plano"
            value={`${item.planCode} (${item.marketCode})`}
          />
        </dl>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!subAccountId.trim()) return;
            onSubmit(subAccountId.trim(), notes.trim() || undefined);
          }}
          className="mt-6 space-y-4"
        >
          <Field
            label="ID da subconta Nelogica/BlackArrow"
            required
            hint="ID interno da conta criada pelo time de operações na plataforma"
          >
            <Input
              value={subAccountId}
              onChange={(e) => setSubAccountId(e.target.value)}
              placeholder="ex: 12345678"
              autoFocus
              required
            />
          </Field>

          <Field label="Notas internas (opcional)">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="border-ink-200 focus:border-brand-500 focus:ring-brand-500/30 w-full rounded-lg border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2"
              placeholder="Ex: contato direto via WhatsApp, particularidade contratual…"
            />
          </Field>

          {errorMessage && (
            <Alert tone="danger" title="Não foi possível ativar">
              {errorMessage}
            </Alert>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={!subAccountId.trim() || submitting}>
              {submitting ? 'Ativando…' : 'Ativar conta'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Row({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-ink-500 mt-0.5">{icon}</span>
      <div className="flex-1">
        <dt className="text-2xs text-ink-500 uppercase tracking-wider">{label}</dt>
        <dd className="text-ink-900 mt-0.5 break-all text-sm font-medium">{value}</dd>
      </div>
    </div>
  );
}
